/**
 * Custom hook for managing Create Chat form state and submission
 * Provides form validation, state management, and chat creation logic.
 *
 * This hook is the SINGLE owner of the create flow's user-facing side effects:
 * success/error/partial-success notifications and navigation. `useCreateChat`
 * returns a discriminated `CreateChatResult` and does nothing user-facing, so
 * ownership never splits across the two layers.
 */

import type { CreateChatResult } from '@choco-admin/types';
import {
  CreateChatFormData,
  CreateChatType,
  CreatedContext,
} from '@choco-admin/types';
import { useCallback, useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';

import { useNotification } from '../../../contexts/NotificationContext';
import { usePuppyMode } from '../../../hooks';
import {
  DEFAULT_CUTOFF_DAYS,
  DEFAULT_CUTOFF_TIMES,
  DEFAULT_CUTOFF_TYPE,
  DEFAULT_DELIVERY_DAYS,
} from '../constants/deliveryDefaults';
import { createChatResolver } from '../validation/createChatValidation';

import { useCreateChat } from './useCreateChat';

// Default form values
const defaultFormValues: CreateChatFormData = {
  // Participants
  buyer: null,
  supplier: null,
  buyerUsers: [],
  supplierUsers: [],
  deselectedOrderPreferences: [],

  // Settings
  chatName: '',
  restaurantName: '',
  deliveryAddress: '',
  customerNumber: '',
  additionalEmails: '',
  chatType: { value: CreateChatType.ORDERING, label: 'Ordering' },
  subCatalog: [],
  createdContext: { value: CreatedContext.ADMIN, label: 'Admin Created' },
  customerLevelMovAmount: null,
  customerLevelMovCurrency: null,
  // Legacy parity: unset until the override checkbox is enabled (which
  // forces it to true); the select shows its placeholder until then.
  visibleChatPrices: undefined,
  isPriceOverrideEnabled: false,
  // eCommerce PO number: off by default; the control only appears for
  // distributors that support it.
  isEcommercePoNumberRequired: false,

  // Delivery settings (company defaults; custom settings are opt-in)
  hasCustomChatDeliverySettings: false,
  deliveryDays: DEFAULT_DELIVERY_DAYS,
  cutoffType: DEFAULT_CUTOFF_TYPE,
  cutoffTimes: DEFAULT_CUTOFF_TIMES,
  cutoffDays: DEFAULT_CUTOFF_DAYS,
};

const getChatDisplayName = (chat: {
  chatName?: string;
  restaurantName?: string;
}): string => chat.chatName || chat.restaurantName || 'New Chat';

export interface UseCreateChatFormResponse extends ReturnType<
  typeof useForm<CreateChatFormData>
> {
  // Additional data and actions
  formData: CreateChatFormData;
  onSubmit: () => void;

  // Loading states
  isLoading: boolean;
  error: Error | null;
}

export const useCreateChatForm = (
  initialValues: Partial<CreateChatFormData> = {}
): UseCreateChatFormResponse => {
  const navigate = useNavigate();
  const { addNotification } = useNotification();
  const { filterMode } = usePuppyMode();
  const isPuppyMode = filterMode === 'puppy';
  const { createChat, isLoading, error } = useCreateChat();

  // Single owner of create-flow notifications + navigation.
  const handleCreateResult = useCallback(
    (result: CreateChatResult): void => {
      switch (result.status) {
        case 'success': {
          addNotification({
            title: 'Chat Created',
            message: `Chat "${getChatDisplayName(
              result.chat
            )}" was successfully created.`,
            type: 'success',
          });
          navigate(`/chats/${result.chat.id}`);
          break;
        }
        case 'partial': {
          // The chat exists but a follow-up setting failed. Name the exact
          // setting, make it explicit the chat was created, and route the
          // operator to it so they do NOT retry the create form and produce a
          // duplicate.
          addNotification({
            title: 'Chat created — finish setup in the chat',
            message: `Chat "${getChatDisplayName(result.chat)}" (ID ${
              result.chat.id
            }) was created, but its ${result.failedSetting} could not be applied: ${
              result.postCreateError.message
            }. The chat already exists — do not retry; opening it so you can finish setup.`,
            type: 'warning',
          });
          navigate(`/chats/${result.chat.id}`);
          break;
        }
        case 'error': {
          // No chat was created — stay on the form so a corrected retry is safe.
          addNotification({
            title: 'Failed to Create Chat',
            message:
              result.error.message ||
              'An unexpected error occurred while creating the chat.',
            type: 'error',
          });
          break;
        }
        default:
          break;
      }
    },
    [addNotification, navigate]
  );

  // Merge default values with initial values; memoized so the object reference is
  // stable between renders and does not retrigger the reset effect on every render.
  const formDefaultValues = useMemo(
    () => ({ ...defaultFormValues, ...initialValues }),
    // Spread of initialValues: we want referential stability, so we JSON-serialize
    // the keys/values as a cheap deep-equality sentinel.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [JSON.stringify(initialValues)]
  );

  const formMethods = useForm<CreateChatFormData>({
    defaultValues: formDefaultValues,
    resolver: createChatResolver,
    mode: 'onChange',
  });

  const { handleSubmit, watch, setValue, reset } = formMethods;
  const formData = watch();

  // Update form values when initial values change (e.g., when duplicate data is loaded).
  // formDefaultValues is now memoized, so this effect only fires when initialValues
  // actually changes — not on every render.
  // Depends on the memoized defaults, not on `initialValues` — the latter's
  // identity changes on every render for callers that build it inline.
  useEffect(() => {
    if (Object.keys(formDefaultValues).length > 0) {
      reset(formDefaultValues);
    }
  }, [formDefaultValues, reset]);

  const hasInitialValues = Object.keys(initialValues).length > 0;

  // Reset buyer/supplier selections when puppy mode changes (but not if we have
  // initial values). Keyed on the boolean, never on the object: keying on the
  // object made this fire every render and setValue re-rendered, which is the
  // loop that broke duplicating a chat.
  useEffect(() => {
    if (!hasInitialValues) {
      setValue('buyer', null);
      setValue('supplier', null);
      setValue('buyerUsers', []);
      setValue('supplierUsers', []);
    }
  }, [isPuppyMode, setValue, hasInitialValues]);

  // handleSubmit only runs the callback when the resolver reports the form
  // valid, so invalid required fields never reach `createChat` — they render
  // field-level errors instead.
  const onSubmit = handleSubmit(async (data: CreateChatFormData) => {
    const result = await createChat(data);
    handleCreateResult(result);
  });

  return {
    // All form methods for FormProvider compatibility
    ...formMethods,

    // Additional data and actions
    formData,
    onSubmit,

    // Loading states
    isLoading: isLoading || formMethods.formState.isSubmitting,
    error,
  };
};
